import type { i18n as I18nInstance } from 'i18next';
import type { SupportedLocale } from '@dukanti/shared/i18n';

const publishFieldDiffResources = {
  ar: {
    admin: {
      storefrontEngine: {
        publishSummary: {
          title: 'تأكيد النشر',
          description: 'راجع الملخص الآمن قبل جعل هذه الواجهة عامة.',
          pendingTitle: 'الحالة المنتظرة',
          changedSurfacesTitle: 'المساحات التي تغيرت',
          changedSectionsTitle: '{{count}} تغييرات في الأقسام',
          changedSectionLine: '{{section}} · {{change}}',
          noChangedSurfaces: 'لم تتغير مساحات على مستوى الصفحة.',
          noChangedSections: 'لم يتم العثور على تغييرات في الأقسام.',
          additionalChangedSections: '{{count}} تغييرات إضافية في الأقسام',
          publicExposureReminder:
            'النشر يستبدل واجهة المتجر العامة للمتسوقين. تبقى المسودات والتغييرات المحلية خاصة حتى تنجح هذه الخطوة.',
          confirm: 'نشر واجهة المتجر',
          labels: {
            draft: 'المسودة',
            localChanges: 'التغييرات المحلية',
            workspaceEdits: 'تغييرات مساحة العمل',
            readiness: 'الجاهزية',
          },
          values: {
            draftExists: 'توجد مسودة محفوظة',
            noDraft: 'لا توجد مسودة محفوظة بعد',
            localChanges: 'سيتم حفظها قبل النشر',
            noLocalChanges: 'لا توجد تغييرات محلية غير محفوظة',
            workspaceEdits: 'طبّقها أو ألغها قبل النشر',
            noWorkspaceEdits: 'لا توجد تغييرات منتظرة',
          },
          surfaces: {
            seo: 'بيانات البحث والمشاركة',
            theme: 'المظهر',
            navigation: 'التنقل وإطار المتجر',
            hero: 'قسم البطل',
            categories: 'الأقسام',
            ads: 'إعلانات المتجر',
            productRails: 'مسارات المنتجات',
            contentSections: 'أقسام المحتوى',
            sectionOrder: 'ترتيب الأقسام',
            sectionLifecycle: 'حالة الأقسام',
          },
          sectionTypes: {
            productRails: 'مسار منتجات',
          },
          changeKinds: {
            added: 'أضيف',
            removed: 'أزيل',
            changed: 'تغير',
            moved: 'تغير ترتيبه',
            statusChanged: 'تغير الظهور',
          },
          fieldDiff: {
            title: 'تفاصيل التغييرات',
            description: 'راجع ما سيتغير للمتسوقين قبل النشر.',
            empty: 'لا توجد تفاصيل حقول آمنة لهذا النشر.',
            baselineUnavailable: 'المقارنة التفصيلية غير متاحة، لكن ملخص النشر الآمن ما زال متاحا.',
            lineWithSection: '{{section}}: {{detail}}',
            additionalChanges: '{{count}} تغييرات آمنة إضافية',
            details: {
              seoTitle: 'تغير عنوان البحث',
              seoDescription: 'تغير وصف البحث',
              seoSocialImage: 'تغيرت صورة المشاركة',
              themePalette: 'تغيرت لوحة المظهر',
              themeColors: 'تغيرت ألوان المظهر',
              themeTypography: 'تغير خط الواجهة',
              themeRadius: 'تغير تدوير الزوايا',
              themeSpacing: 'تغير تباعد الواجهة',
              chromeHeaderBrand: 'تغير عرض اسم أو شعار المتجر في الترويسة',
              chromeHeaderLinks: 'تغيرت روابط الترويسة',
              chromeFooterBrand: 'تغير عرض العلامة في التذييل',
              chromeFooterLinks: 'تغيرت روابط التذييل',
              chromeFooterContact: 'تغيرت طرق التواصل في التذييل',
              chromeFooterSocial: 'تغيرت روابط التواصل الاجتماعي في التذييل',
              chromeMobileItems: 'تغيرت عناصر التنقل في الهاتف',
              chromeAnnouncementBarText: 'تغير نص شريط الإعلانات',
              chromeAnnouncementBarLink: 'تغير رابط شريط الإعلانات',
              sectionAdded: 'سيتم إضافة القسم',
              sectionRemoved: 'سيتم إزالة القسم',
              sectionDraftDeleted: 'تم تعليم القسم للحذف من المسودة',
              sectionHidden: 'سيتم إخفاء القسم',
              sectionShown: 'سيتم إظهار القسم',
              sectionStatus: 'تغيرت حالة القسم',
              sectionMoved: 'تغير ترتيب القسم',
              sectionDeviceVisibility: 'تغير ظهور القسم حسب الجهاز',
              sectionSettings: 'تغيرت إعدادات القسم',
              sectionPresentation: 'تغير عرض القسم',
              sectionImage: 'تغيرت صورة القسم',
              sectionLimit: 'تغير حد العناصر الظاهرة',
              heroSlideAdded: 'أضيفت شريحة بطل',
              heroSlideRemoved: 'أزيلت شريحة بطل',
              heroSlideReordered: 'تغير ترتيب شرائح البطل',
              heroHeadline: 'تغير عنوان البطل',
              heroSubheadline: 'تغير النص الفرعي للبطل',
              heroDescription: 'تغير وصف البطل',
              heroButtonLabel: 'تغير نص زر البطل',
              heroButtonDestination: 'تغير هدف زر البطل',
              heroDesktopImage: 'تغيرت صورة البطل على سطح المكتب',
              heroMobileImage: 'تغيرت صورة البطل على الهاتف',
              heroSchedule: 'تغيرت جدولة البطل',
              heroPresentation: 'تغير عرض البطل',
              sourceType: 'تغير مصدر المحتوى',
              manualSelectionCount: 'تغير عدد العناصر المختارة يدويا',
              manualSelectionOrder: 'تغير ترتيب العناصر المختارة يدويا',
              limit: 'تغير حد العناصر الظاهرة',
              layout: 'تغير التخطيط',
              density: 'تغيرت كثافة العرض',
              imagePresentation: 'تغير عرض الصور',
              cardDisplay: 'تغيرت تفاصيل البطاقات',
              viewAllDestination: 'تغير هدف عرض الكل',
              destinationCue: 'تغير تلميح الوجهة',
              contentItemCount: 'تغير عدد عناصر المحتوى',
              contentItemOrder: 'تغير ترتيب عناصر المحتوى',
              contentTitle: 'تغير عنوان المحتوى',
              contentSubtitle: 'تغير النص الفرعي للمحتوى',
              contentBody: 'تغير نص المحتوى',
              contentQuestion: 'تغير سؤال في المحتوى',
              contentAnswer: 'تغير جواب في المحتوى',
              contentTestimonial: 'تغير نص شهادة مكتوبة',
              contentCtaLabel: 'تغير نص زر المحتوى',
              contentButtonDestination: 'تغير هدف زر المحتوى',
              contentIcon: 'تغيرت أيقونة المحتوى',
              contentPresentation: 'تغير عرض المحتوى',
            },
          },
        },
      },
    },
  },
  he: {
    admin: {
      storefrontEngine: {
        publishSummary: {
          title: 'אישור פרסום',
          description: 'בדקו את הסיכום הבטוח לפני שחזית החנות הופכת לציבורית.',
          pendingTitle: 'מצב ממתין',
          changedSurfacesTitle: 'אזורים שהשתנו',
          changedSectionsTitle: '{{count}} שינויי מדורים',
          changedSectionLine: '{{section}} · {{change}}',
          noChangedSurfaces: 'לא השתנו אזורים ברמת העמוד.',
          noChangedSections: 'לא זוהו שינויי מדורים.',
          additionalChangedSections: '{{count}} שינויי מדורים נוספים',
          publicExposureReminder:
            'פרסום מחליף את חזית החנות הציבורית לקונים. טיוטות ושינויים מקומיים נשארים פרטיים עד שהצעד הזה מצליח.',
          confirm: 'פרסם חזית חנות',
          labels: {
            draft: 'טיוטה',
            localChanges: 'שינויים מקומיים',
            workspaceEdits: 'שינויי סביבת עבודה',
            readiness: 'מוכנות',
          },
          values: {
            draftExists: 'יש טיוטה שמורה',
            noDraft: 'עדיין אין טיוטה שמורה',
            localChanges: 'יישמרו לפני הפרסום',
            noLocalChanges: 'אין שינויים מקומיים שלא נשמרו',
            workspaceEdits: 'יש להחיל או לבטל לפני פרסום',
            noWorkspaceEdits: 'אין שינויים ממתינים',
          },
          surfaces: {
            seo: 'חיפוש ושיתוף',
            theme: 'עיצוב',
            navigation: 'ניווט ומעטפת',
            hero: 'הירו',
            categories: 'קטגוריות',
            ads: 'מודעות החנות',
            productRails: 'מסילות מוצרים',
            contentSections: 'מדורי תוכן',
            sectionOrder: 'סדר מדורים',
            sectionLifecycle: 'מצב מדורים',
          },
          sectionTypes: {
            productRails: 'מסילת מוצרים',
          },
          changeKinds: {
            added: 'נוסף',
            removed: 'הוסר',
            changed: 'השתנה',
            moved: 'הועבר',
            statusChanged: 'נראות השתנתה',
          },
          fieldDiff: {
            title: 'פרטי השינויים',
            description: 'בדקו מה ישתנה לקונים לפני הפרסום.',
            empty: 'אין פרטי שדות בטוחים לפרסום הזה.',
            baselineUnavailable: 'השוואה מפורטת אינה זמינה, אך סיכום הפרסום הבטוח עדיין זמין.',
            lineWithSection: '{{section}}: {{detail}}',
            additionalChanges: '{{count}} שינויים בטוחים נוספים',
            details: {
              seoTitle: 'כותרת החיפוש השתנתה',
              seoDescription: 'תיאור החיפוש השתנה',
              seoSocialImage: 'תמונת השיתוף השתנתה',
              themePalette: 'פלטת העיצוב השתנתה',
              themeColors: 'צבעי העיצוב השתנו',
              themeTypography: 'טיפוגרפיית החנות השתנתה',
              themeRadius: 'עיגול הפינות השתנה',
              themeSpacing: 'מרווחי החנות השתנו',
              chromeHeaderBrand: 'תצוגת שם או לוגו החנות בכותרת השתנתה',
              chromeHeaderLinks: 'קישורי הכותרת השתנו',
              chromeFooterBrand: 'תצוגת המותג בתחתית השתנתה',
              chromeFooterLinks: 'קישורי התחתית השתנו',
              chromeFooterContact: 'אפשרויות הקשר בתחתית השתנו',
              chromeFooterSocial: 'קישורי הרשתות החברתיות בתחתית השתנו',
              chromeMobileItems: 'פריטי הניווט בנייד השתנו',
              chromeAnnouncementBarText: 'טקסט רצועת ההודעות השתנה',
              chromeAnnouncementBarLink: 'קישור רצועת ההודעות השתנה',
              sectionAdded: 'המדור יתווסף',
              sectionRemoved: 'המדור יוסר',
              sectionDraftDeleted: 'המדור סומן למחיקה מהטיוטה',
              sectionHidden: 'המדור יוסתר',
              sectionShown: 'המדור יוצג',
              sectionStatus: 'מצב המדור השתנה',
              sectionMoved: 'סדר המדור השתנה',
              sectionDeviceVisibility: 'נראות המדור לפי מכשיר השתנתה',
              sectionSettings: 'הגדרות המדור השתנו',
              sectionPresentation: 'תצוגת המדור השתנתה',
              sectionImage: 'תמונת המדור השתנתה',
              sectionLimit: 'מגבלת הפריטים המוצגים השתנתה',
              heroSlideAdded: 'שקופית הירו נוספה',
              heroSlideRemoved: 'שקופית הירו הוסרה',
              heroSlideReordered: 'סדר שקופיות ההירו השתנה',
              heroHeadline: 'כותרת ההירו השתנתה',
              heroSubheadline: 'כותרת המשנה של ההירו השתנתה',
              heroDescription: 'תיאור ההירו השתנה',
              heroButtonLabel: 'טקסט כפתור ההירו השתנה',
              heroButtonDestination: 'יעד כפתור ההירו השתנה',
              heroDesktopImage: 'תמונת ההירו לדסקטופ השתנתה',
              heroMobileImage: 'תמונת ההירו לנייד השתנתה',
              heroSchedule: 'תזמון ההירו השתנה',
              heroPresentation: 'תצוגת ההירו השתנתה',
              sourceType: 'מקור התוכן השתנה',
              manualSelectionCount: 'מספר הפריטים שנבחרו ידנית השתנה',
              manualSelectionOrder: 'סדר הפריטים שנבחרו ידנית השתנה',
              limit: 'מגבלת הפריטים המוצגים השתנתה',
              layout: 'הפריסה השתנתה',
              density: 'צפיפות התצוגה השתנתה',
              imagePresentation: 'תצוגת התמונות השתנתה',
              cardDisplay: 'פרטי הכרטיסים השתנו',
              viewAllDestination: 'יעד הצגת הכל השתנה',
              destinationCue: 'רמז היעד השתנה',
              contentItemCount: 'מספר פריטי התוכן השתנה',
              contentItemOrder: 'סדר פריטי התוכן השתנה',
              contentTitle: 'כותרת התוכן השתנתה',
              contentSubtitle: 'טקסט המשנה של התוכן השתנה',
              contentBody: 'גוף התוכן השתנה',
              contentQuestion: 'שאלת תוכן השתנתה',
              contentAnswer: 'תשובת תוכן השתנתה',
              contentTestimonial: 'טקסט המלצה כתוב השתנה',
              contentCtaLabel: 'טקסט כפתור התוכן השתנה',
              contentButtonDestination: 'יעד כפתור התוכן השתנה',
              contentIcon: 'אייקון התוכן השתנה',
              contentPresentation: 'תצוגת התוכן השתנתה',
            },
          },
        },
      },
    },
  },
  en: {
    admin: {
      storefrontEngine: {
        publishSummary: {
          title: 'Confirm publish',
          description: 'Review the safe summary before making this storefront public.',
          pendingTitle: 'Pending state',
          changedSurfacesTitle: 'Changed surfaces',
          changedSectionsTitle: '{{count}} section changes',
          changedSectionLine: '{{section}} · {{change}}',
          noChangedSurfaces: 'No page-level surfaces changed.',
          noChangedSections: 'No section-level changes detected.',
          additionalChangedSections: '{{count}} more section changes',
          publicExposureReminder:
            'Publishing replaces the public storefront for shoppers. Drafts and local edits stay private until this step succeeds.',
          confirm: 'Publish storefront',
          labels: {
            draft: 'Draft',
            localChanges: 'Local changes',
            workspaceEdits: 'Workspace edits',
            readiness: 'Readiness',
          },
          values: {
            draftExists: 'Saved draft available',
            noDraft: 'No saved draft yet',
            localChanges: 'Will save before publishing',
            noLocalChanges: 'No unsaved local changes',
            workspaceEdits: 'Apply or cancel before publishing',
            noWorkspaceEdits: 'None pending',
          },
          surfaces: {
            seo: 'Search and sharing',
            theme: 'Theme',
            navigation: 'Navigation and chrome',
            hero: 'Hero',
            categories: 'Categories',
            ads: 'Store ads',
            productRails: 'Product rails',
            contentSections: 'Content sections',
            sectionOrder: 'Section order',
            sectionLifecycle: 'Section lifecycle',
          },
          sectionTypes: {
            productRails: 'Product rail',
          },
          changeKinds: {
            added: 'Added',
            removed: 'Removed',
            changed: 'Changed',
            moved: 'Moved',
            statusChanged: 'Visibility changed',
          },
          fieldDiff: {
            title: 'Change details',
            description: 'Review what will change for shoppers before publishing.',
            empty: 'No safe field-level details are available for this publish.',
            baselineUnavailable:
              'A detailed comparison is not available, but the safe publish summary is still available.',
            lineWithSection: '{{section}}: {{detail}}',
            additionalChanges: '{{count}} more safe changes',
            details: {
              seoTitle: 'Search title changed',
              seoDescription: 'Search description changed',
              seoSocialImage: 'Sharing image changed',
              themePalette: 'Theme palette changed',
              themeColors: 'Theme colors changed',
              themeTypography: 'Storefront typography changed',
              themeRadius: 'Corner rounding changed',
              themeSpacing: 'Storefront spacing changed',
              chromeHeaderBrand: 'Header store name or logo display changed',
              chromeHeaderLinks: 'Header links changed',
              chromeFooterBrand: 'Footer brand display changed',
              chromeFooterLinks: 'Footer links changed',
              chromeFooterContact: 'Footer contact methods changed',
              chromeFooterSocial: 'Footer social links changed',
              chromeMobileItems: 'Mobile navigation items changed',
              chromeAnnouncementBarText: 'Announcement bar text changed',
              chromeAnnouncementBarLink: 'Announcement bar link changed',
              sectionAdded: 'Section will be added',
              sectionRemoved: 'Section will be removed',
              sectionDraftDeleted: 'Section is marked for draft deletion',
              sectionHidden: 'Section will be hidden',
              sectionShown: 'Section will be shown',
              sectionStatus: 'Section status changed',
              sectionMoved: 'Section order changed',
              sectionDeviceVisibility: 'Device visibility changed',
              sectionSettings: 'Section settings changed',
              sectionPresentation: 'Section presentation changed',
              sectionImage: 'Section image changed',
              sectionLimit: 'Visible item limit changed',
              heroSlideAdded: 'Hero slide added',
              heroSlideRemoved: 'Hero slide removed',
              heroSlideReordered: 'Hero slides reordered',
              heroHeadline: 'Hero headline changed',
              heroSubheadline: 'Hero supporting text changed',
              heroDescription: 'Hero description changed',
              heroButtonLabel: 'Hero button label changed',
              heroButtonDestination: 'Hero button destination changed',
              heroDesktopImage: 'Desktop hero image changed',
              heroMobileImage: 'Mobile hero image changed',
              heroSchedule: 'Hero schedule changed',
              heroPresentation: 'Hero presentation changed',
              sourceType: 'Content source changed',
              manualSelectionCount: 'Manual selection count changed',
              manualSelectionOrder: 'Manual selection order changed',
              limit: 'Visible item limit changed',
              layout: 'Layout changed',
              density: 'Display density changed',
              imagePresentation: 'Image presentation changed',
              cardDisplay: 'Card details changed',
              viewAllDestination: 'View-all destination changed',
              destinationCue: 'Destination cue changed',
              contentItemCount: 'Content item count changed',
              contentItemOrder: 'Content item order changed',
              contentTitle: 'Content title changed',
              contentSubtitle: 'Content supporting text changed',
              contentBody: 'Content body changed',
              contentQuestion: 'Content question changed',
              contentAnswer: 'Content answer changed',
              contentTestimonial: 'Written testimonial text changed',
              contentCtaLabel: 'Content button label changed',
              contentButtonDestination: 'Content button destination changed',
              contentIcon: 'Content icon changed',
              contentPresentation: 'Content presentation changed',
            },
          },
        },
      },
    },
  },
} as const satisfies Record<SupportedLocale, object>;

export function installStorefrontPublishFieldDiffI18n(instance: I18nInstance): void {
  for (const [locale, resources] of Object.entries(publishFieldDiffResources) as Array<
    [SupportedLocale, (typeof publishFieldDiffResources)[SupportedLocale]]
  >) {
    instance.addResourceBundle(locale, 'translation', resources, true, true);
  }
}
